import { AgmMap, MapsAPILoader } from '@agm/core';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ClusterStyle } from '@agm/js-marker-clusterer/services/google-clusterer-types';
import crossfilter from 'crossfilter2/crossfilter';
import { get, orderBy } from 'lodash';
import { SelectItem } from 'primeng/api';
import { Table, LazyLoadEvent } from 'primeng';
import { IFacetedInboxTask } from '../../interfaces/faceted-inbox-task';
import { LoggerService } from '../../services';
import { TaggedLogger } from '../../services';
import { VmeInboxJobsService } from '../../services';
import { Location } from '@angular/common';
import moment from 'moment';

@Component({
  selector: 'vme-jobs-faceted-filtering',
  templateUrl: 'vme-jobs-faceted-filtering.component.html',
  styleUrls: ['vme-jobs-faceted-filtering.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})

//Crossfilter documentation: https://github.com/crossfilter/crossfilter
export class VMEJobsFacetedFilteringComponent implements OnInit, AfterViewChecked {
  private logger: TaggedLogger;

  @Output() jobSelectionChanged = new EventEmitter<any>();
  @Output() onGridAction = new EventEmitter<any>();

  public type_inboxSupplierToBeConfirmed = 1;
  public type_inboxCombine = 4;
  public type_inboxDeploy = 11;

  latitude: number;
  longitude: number;
  zoom: number = 10;
  gmap: any;
  showHeatmap: boolean = false;
  showMap: boolean = false;

  mapLoaded: boolean = false;
  mapUpdating: boolean = false;
  awaitMap: boolean = false;
  loadingSelection: boolean = false;

  polylinePoints: any[];
  latLngPoints: any[];

  // the entire resultset
  allData: IFacetedInboxTask[];

  // the crossfiltered resultset
  crossFilterData: any;

  // each group creates a selection table in the UI
  filterGroups: any[];

  // the dimensions of the crossfiltered data
  filterDimensions: any[];

  // the selected filter items in each list
  selectedFilters: any[] = [];
  currentSelection; // a modified cut-down version of selectedFilters for saving and loading

  // this is all data filtered by the currently selected items within dimensions
  // crossfilter uses this to create dimensions which will then reflect the selected items
  workingSet: IFacetedInboxTask[] = [];
  resultSet: IFacetedInboxTask[]; // the rows shown in the paginated grid

  // sort on value descending or key ascending
  sortType: boolean[] = [];

  // selected job in the results grid
  selectedJob: IFacetedInboxTask;

  // paginated grid properties
  rowsInPage: number = 10;
  firstRow: number = 0;
  sortField: string;
  sortOrder: number = 0;

  isLoading: boolean = true;
  showLogging: boolean = false;

  // this controls whether the search filter per facet (if any) is applied when ngAfterContentChecked is run
  // the facets listview will not respond if called from any other method since it needs to wait for content to be recreated in the view
  refreshTableSearchFilter: boolean = false;

  pickupIconStyles: ClusterStyle[];
  dropoffIconStyles: ClusterStyle[];

  public showAllJobs = false;
  showInspectionTasks = false;

  noRowReset = false; // if true when refreshing grid data the firstRow is not reset to 0

  // unused filters with rangeDivisor
  //{
  //    propertyName: "vehicleWeight", title: "Weight", rangeDivisor: 1000, descriptor: 'kg'
  //},
  //{
  //    propertyName: "moveDistance", title: "Journey Length", rangeDivisor: 50, descriptor: ' miles'
  //},

  filterDefinitions: any[] = [
    {
      propertyName: 'customerName',
      title: 'Customer',
      searchable: true,
    },
    {
      propertyName: 'customerDepartment',
      title: 'Department',
      searchable: true,
    },
    {
      propertyName: 'supplierName',
      title: 'Supplier',
      searchable: true,
    },
    // {
    //   propertyName: "priorityDeployment", title: "Priority Deployment", searchable: false
    // },
    {
      propertyName: 'deploymentPriority',
      title: 'Deployment Priority',
      searchable: false,
    },
    {
      propertyName: 'supplierContractType',
      title: 'Supplier Contract Type',
    },
    {
      propertyName: 'orderCreatedDate',
      title: 'Order Date',
      isDate: true,
      noRangeCheck: true,
    },
    {
      propertyName: 'pickupAreaName',
      title: 'Collection Region',
      searchable: true,
    },
    {
      propertyName: 'pickupZoneName',
      title: 'Collection Area',
      searchable: true,
    },
    {
      propertyName: 'deliveryAreaName',
      title: 'Delivery Region',
      searchable: true,
    },
    {
      propertyName: 'deliveryZoneName',
      title: 'Delivery Area',
      searchable: true,
    },
    {
      propertyName: 'combinationType',
      title: 'Task Combination',
    },
    {
      propertyName: 'scheduledDate',
      title: 'Scheduled Date',
      isDate: true,
    },
    {
      propertyName: 'customerTaskType',
      title: 'Move Type',
    },
    {
      propertyName: 'inboxTabName',
      title: 'Inbox',
    },
    {
      propertyName: 'jobStage',
      title: 'Job Stage',
    },
    {
      propertyName: 'latestPickupDate',
      title: 'Days Left',
      dateDiff: true,
      tooltip: 'Days left until latest pickup date',
    },
    {
      propertyName: 'latestPickupDate',
      title: 'Latest Collection',
      isDate: true,
      noRangeCheck: true,
    },
    {
      propertyName: 'latestDropOffDate',
      title: 'Latest Delivery',
      isDate: true,
      noRangeCheck: true,
    },
    {
      propertyName: 'vehicleWeight',
      title: 'Weight Category',
      ranges: [
        { min: 1, max: 3500, groupValue: 1, label: 'Less than 3.5t' },
        { min: 3500, max: 7500, groupValue: 3.5, label: '3.5t - 7.5t' },
        { min: 7500, max: 18000, groupValue: 7.5, label: '7.5t - 18t' },
        { min: 18000, max: 44000, groupValue: 18, label: '18t - 44t' },
      ],
    },
    {
      propertyName: 'moveDistance',
      title: 'Journey Length',
      ranges: [
        { min: 0, max: 10, groupValue: 1, label: 'Up to 10 miles' },
        { min: 10, max: 20, groupValue: 2, label: '10 - 20 miles' },
        { min: 20, max: 50, groupValue: 3, label: '20 - 50 miles' },
        { min: 50, max: 100, groupValue: 4, label: '50 - 100 miles' },
        { min: 100, max: 200, groupValue: 5, label: '100 - 200 miles' },
        { min: 200, max: 300, groupValue: 6, label: '200 - 300 miles' },
        { min: 300, max: 400, groupValue: 7, label: '300 - 400 miles' },
        { min: 400, max: 10000, groupValue: 8, label: '400+ miles' },
      ],
    },
    {
      propertyName: 'checklistCategoryName',
      title: 'Checklist Category',
    },
    {
      propertyName: 'checklistSubCategoryName',
      title: 'Checklist Subcategory',
    },
    {
      propertyName: 'carryOverAuthorised',
      title: 'Carry-Over Authorised',
    },
    {
      propertyName: 'carryoverApprovalStatus',
      title: 'Carry-Over Approval Status',
    },
    {
      propertyName: 'classification',
      title: 'Supplier Classification',
    },
    {
      propertyName: 'taskStatusName',
      title: 'Task Status',
    },
    {
      propertyName: 'isMoDelMove',
      title: 'Is MoDel Move',
    },
    {
      propertyName: 'isDelayed',
      title: 'Customer Update',
    },
    {
      propertyName: 'vehicleFuelType',
      title: 'Fuel Type',
    },
    {
      propertyName: 'retailStatus',
      title: 'Retail Status',
      searchable: false,
    },
    {
      propertyName: 'earliestPickupDate',
      title: 'Earliest Collection',
      isDate: true,
      noRangeCheck: true,
    },
  ];

  @ViewChildren('facetTable') facetTables: QueryList<Table>;
  @ViewChild(AgmMap) map: AgmMap;

  public originIcon = {
    url: 'assets/images/pickup_map_icon.png',
    scaledSize: { width: 48, height: 48 },
  };
  public destinationIcon = {
    url: 'assets/images/dropoff_map_icon.png',
    scaledSize: { width: 48, height: 48 },
  };

  //private heatmap: google.maps.visualization.HeatmapLayer = null;
  private heatmap: any;
  settingFilters: boolean = false;

  constructor(
    private router: Router,
    private vmeInboxjobsService: VmeInboxJobsService,
    private mapsAPILoader: MapsAPILoader,
    private changeDetectorRef: ChangeDetectorRef,
    loggerService: LoggerService,
    private activatedRoute: ActivatedRoute,
    private readonly location: Location,
  ) {
    this.logger = loggerService.taggedLogger('FacetedFiltering');
  }

  async ngOnInit() {
    this.logger.trace('OnInit');
    this.latitude = 52.5;
    this.longitude = -2.0;

    this.isLoading = true;

    const storedShowInspectionTasksValue = JSON.parse(localStorage.getItem('showInspectionTasks'));
    if (storedShowInspectionTasksValue) {
      this.showInspectionTasks = storedShowInspectionTasksValue;
    }

    await this.load();
  }

  ngAfterViewChecked() {
    this.logger.trace('AfterViewChecked');
    if (this.refreshTableSearchFilter) {
      for (let index = 0; index < this.filterDefinitions.length; index++) {
        if (this.filterDefinitions[index].tableFilter) {
          let tf = this.filterDefinitions[index].tableFilter;
          let facetTable = this.facetTables.toArray()[index];

          //console.log('Filtering by search: ', tf, ' - table: ', facetTable);
          facetTable.filter(tf.value, tf.field, tf.method);
        }
      }

      this.refreshTableSearchFilter = false;
    }
  }

  clearDimensions() {
    //console.log("Filter dimensions: ", this.filterDimensions);

    for (let i = 0; i < this.filterDimensions.length; i++) {
      if (!this.selectedFilters[i]) this.filterDimensions[i].filterAll();
    }
  }

  filterRowSelectionChanged(index: number) {
    if (this.showMap) {
      if (this.showLogging) console.log('Map updating.. spinner should show now');
      this.mapUpdating = true;
      this.awaitMap = true;
    }

    this.loadingSelection = true;

    this.clearDimensions();
    this.setDimensionFilter(index);

    //console.log("Calling populateWorkingSet from filterRowSelectionChanged");
    this.populateWorkingSet();

    //console.log("Working set: ", this.workingSet);
    this.loadingSelection = false;
  }

  setDimensionFilter(index: number) {
    this.logger.trace(
      `Setting dimension filters on ${index} - selected: `,
      this.selectedFilters[index],
    );

    //flatten the values in the selection array
    const selectedFilter = get(this.selectedFilters, `[${index}]`);

    const selectedFilterValues =
      selectedFilter && typeof selectedFilter.map === 'function'
        ? selectedFilter.map((x) => {
            return x.value;
          })
        : [];

    //console.log("Selected filters: ", this.selectedFilters[index], "   - values: ", values);

    if (selectedFilterValues && selectedFilterValues.length > 0) {
      //console.log("Current filter definition: ", this.filterDefinitions[index]);

      let definition = this.filterDefinitions[index];
      if (!definition) return;

      if (!definition.rangeDivisor && !definition.dateDiff && !definition.ranges) {
        //console.log("Filtering exact, value: ", this.selectedFilters[index].value);
        //console.log("Filtering exact, value: ", selectedFilterValues);

        if (!definition.isDate) {
          this.filterDimensions[index].filterFunction((dv) => {
            return selectedFilterValues.indexOf(dv) >= 0;
          });
        } else {
          this.filterDimensions[index].filterFunction((x) => {
            return selectedFilterValues.indexOf(x.toISOString()) >= 0;
          });
        }
      } else if (definition.rangeDivisor) {
        //console.log("Filtering range, min: ", min, " - max: ", max);
        //this.filterDimensions[index].filterRange([min, max]);

        this.filterDimensions[index].filterFunction((d) => {
          selectedFilterValues.forEach((value) => {
            let min = value - (definition.rangeDivisor - 1);
            let max = value + 1;

            const result = d >= min && d <= max;
            if (result) return result;
          });

          return false;
        });
      } else if (definition.dateDiff) {
        //console.log("Filtering datediff");

        this.filterDimensions[index].filterFunction((x) => {
          let today = moment().startOf('day');
          let xDate = moment(x).startOf('day');
          let days = xDate.diff(today, 'days');

          return selectedFilterValues.indexOf(days) >= 0;
        });
      } else if (definition.ranges) {
        this.filterDimensions[index].filterFunction((x) => {
          let result: boolean = false;

          // get min and max range from value 1
          for (let i = 0; i < definition.ranges.length; i++) {
            let range = definition.ranges[i];

            //this.selectedFilters[index].value == range.groupValue;

            result =
              x >= range.min &&
              x < range.max &&
              selectedFilterValues.indexOf(range.groupValue) >= 0;

            //console.log("x: ", x, " - range: ", range, " - values: ", values, " - ** Result: ", result);
            if (result) break;
          }

          //console.log("Final Result: ", result);
          return result;
        });
      }
    } else if (this.filterDimensions[index]) {
      this.filterDimensions[index].filterAll();
    }

    //console.log("Filter definition {", i, "}: ", this.filterDefinitions[i]);
    this.refreshTableSearchFilter = true;
  }

  populateWorkingSet(saveSelection: boolean = true) {
    // reset groups according to filtered dimensions
    //console.log("Calling setSelections from populateWorkingSet");
    this.setSelections();

    // repopulate the results grid using the selected dimension filters
    this.workingSet = this.crossFilterData.allFiltered();

    //console.log("Working set in populateWorkingSet: ", this.workingSet);
    if (!this.noRowReset) {
      this.firstRow = 0;
    }
    //console.log("Calling setResults from populateWorkingSet, workingSet: ", this.workingSet);
    this.setResults();

    if (saveSelection) this.saveToStorage();

    if (this.showMap) {
      this.setMapsContent();
    }
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async setMapsContent() {
    if (this.showLogging) console.log('Setting map content - map loaded: ', this.mapLoaded);

    // generate polyline points from working set
    if (this.mapLoaded) {
      if (this.awaitMap) {
        await this.sleep(0.125);
        this.awaitMap = false;
      }

      this.polylinePoints = [];
      this.latLngPoints = [];

      let job = this.selectedJob;
      if (job) {
        this.polylinePoints.push({
          lat1: job.fromLatitude,
          lng1: job.fromLongitude,
          lat2: job.toLatitude,
          lng2: job.toLongitude,
        });

        let p1 = new google.maps.LatLng(job.fromLatitude, job.fromLongitude);
        let p2 = new google.maps.LatLng(job.toLatitude, job.toLongitude);

        this.latLngPoints.push(p1, p2);
      } else {
        await this.workingSet.forEach((x) => {
          this.polylinePoints.push({
            lat1: x.fromLatitude,
            lng1: x.fromLongitude,
            lat2: x.toLatitude,
            lng2: x.toLongitude,
          });

          let p1 = new google.maps.LatLng(x.fromLatitude, x.fromLongitude);
          let p2 = new google.maps.LatLng(x.toLatitude, x.toLongitude);

          this.latLngPoints.push(p1, p2);
        });
      }

      //this.showHeatmap = false; //this.latLngPoints.length >= 100;

      //if (this.heatmap && this.showHeatmap) {
      //    this.heatmap.setData(this.latLngPoints);
      //    console.log("Set heatmap data: ", this.latLngPoints);
      //}
      //this.heatmap.set('opacity', this.showHeatmap ? 0.8 : 0);

      await this.fitMapToBounds();
    }
  }

  calculateClusterFunction(markers, numStyles) {
    //create an index for icon styles
    let index = 0; //Count the total number of markers in this cluster
    let count = markers.length;
    //Set total to loop through (starts at total number)
    let total = count;

    /**
     * While we still have markers, divide by a set number and
     * increase the index. Cluster moves up to a new style.
     *
     * The bigger the index, the more markers the cluster contains,
     * so the bigger the cluster.
     */
    while (total > 0) {
      //Create a new total by dividing by a set number
      total = Math.round(total / 5);

      //Increase the index and move up to the next style
      index++;
    }

    /**
     * Make sure we always return a valid index. E.g. If we only have
     * 5 styles, but the index is 8, this will make sure we return
     * 5. Returning an index of 8 wouldn't have a marker style.
     */
    index = Math.min(index, numStyles);

    //Tell MarkerCluster this clusters details (and how to style it)
    return {
      text: count, // for debugging show the image index for cluster + " (" + index + ")",
      index: index,
    };
  }

  fitMapToBounds() {
    let points = this.latLngPoints;

    if (points) {
      let bounds = new google.maps.LatLngBounds();
      for (let i = 0, LtLgLen = points.length; i < LtLgLen; i++) {
        bounds.extend(points[i]);
      }

      this.gmap.fitBounds(bounds);
    }
  }

  async load() {
    this.logger.trace('load()');
    this.isLoading = true;

    this.filterGroups = [];
    this.filterDimensions = [];
    this.allData = await this.vmeInboxjobsService.getAllJobs(
      this.showAllJobs,
      this.showInspectionTasks,
    );

    // console.log('All Data: ', this.allData);

    // setup all the groups for the current tab
    this.crossFilterData = crossfilter(this.allData);
    this.workingSet = this.crossFilterData.allFiltered();

    this.setDimensions();
    this.setResults();

    this.isLoading = false;

    this.loadFromStorage();
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.firstRow = event.first;
    this.rowsInPage = event.rows;
    this.sortField = event.sortField;
    this.sortOrder = event.sortOrder;

    //console.log("Lazy load event: ", event);

    // get data from workingset according to row values
    if (this.workingSet && this.workingSet.length > 0) this.setResults();

    //console.log("Result set: ", this.resultSet, " - event: ", event);
  }

  toggleMap() {
    this.showMap = !this.showMap;
    if (this.showMap) {
      this.mapUpdating = true;
      this.changeDetectorRef.detectChanges();

      // update map to reflect current selections
      this.setMapsContent();
    }
  }

  getResultHeader() {
    if (this.workingSet.length == 1) return '1 Result';
    else return this.workingSet.length + ' Results';
  }

  setResults() {
    //console.log("Sort field: ", this.sortField, " - order: ", this.sortOrder);
    if (this.showLogging) {
      console.log(
        'First row: ',
        this.firstRow,
        ' - rows in page: ',
        this.rowsInPage,
        ' - workingSet: ',
        this.workingSet,
      );
    }

    this.resultSet = this.workingSet
      .sort((a, b) => {
        switch (this.sortField) {
          case 'icon':
            return this.numberSort(
              a.openIncidentCount + a.openMessageCount,
              b.openIncidentCount + b.openMessageCount,
            );
          case 'scheduledDate':
            return this.dateSort(a.scheduledDate, b.scheduledDate);
          case 'jobNumber':
            return this.stringSort(a.jobNumber, b.jobNumber);
          case 'vehicleReg':
            return this.stringSort(a.vehicleReg, b.vehicleReg);
          case 'fromCity':
            return this.stringSort(a.fromCity, b.fromCity);
          case 'toCity':
            return this.stringSort(a.toCity, b.toCity);
          case 'from':
            return this.stringSort(a.from, b.from);
          case 'to':
            return this.stringSort(a.to, b.to);
          case 'pickupZoneName':
            return this.stringSort(a.pickupZoneName, b.pickupZoneName);
          case 'deliveryZoneName':
            return this.stringSort(a.deliveryZoneName, b.deliveryZoneName);
          case 'earliestPickupDate':
            return this.dateSort(a.earliestPickupDate, b.earliestPickupDate);
          case 'earliestDropOffDate':
            return this.dateSort(a.earliestDropOffDate, b.earliestDropOffDate);
          case 'customerTaskType':
            return this.stringSort(a.customerTaskType, b.customerTaskType);
          case 'customerName':
            return this.stringSort(a.customerName, b.customerName);
          case 'supplierName':
            return this.stringSort(a.supplierName, b.supplierName);
          case 'supplierPrice':
            return this.numberSort(a.supplierPrice, b.supplierPrice);
          case 'jobStatus':
            return this.stringSort(a.jobStatus, b.jobStatus);
          case 'inboxTabName':
            return this.stringSort(a.inboxTabName, b.inboxTabName);
          case 'jobStage':
            return this.stringSort(a.jobStage, b.jobStage);
          default:
            return this.dateSort(a.scheduledDate, b.scheduledDate);
        }
      })
      .slice(this.firstRow, this.firstRow + this.rowsInPage);

    //console.log("Working set: ", this.workingSet, " - Result set: ", this.resultSet);

    // clear all filters if no results (can happen with data changes)
    if (this.resultSet.length == 0) {
      //console.log("Result set empty: ", this.resultSet, " - clearing selections");
      //this.clearAllFilters();
    }
  }

  stringSort(a, b): number {
    //console.log("String sort");

    let str1 = !a ? '' : a.toString();
    let str2 = !b ? '' : b.toString();

    if (this.sortOrder == 1) return str1.localeCompare(str2);
    else return str2.localeCompare(str1);
  }

  dateSort(a, b): number {
    //console.log("Date sort");

    if (this.sortOrder == 1) return moment(a).unix() - moment(b).unix();
    else return moment(b).unix() - moment(a).unix();
  }

  numberSort(a, b): number {
    //console.log("Number sort");

    return this.sortOrder == 1 ? a - b : b - a;
  }

  setDimensions() {
    let nullData = 'Not set';
    let nullDataNumber = 0;

    for (let i = 0; i < this.filterDefinitions.length; i++) {
      let def = this.filterDefinitions[i];

      this.filterDimensions[i] = this.crossFilterData.dimension((d) => {
        return (
          (def.isDate ? moment(d[def.propertyName]).startOf('day') : d[def.propertyName]) ||
          (def.ranges ? nullDataNumber : nullData)
        );
      });
    }

    //console.log("Calling setSelections from setDimensions");
    this.setSelections();
  }

  setSelections() {
    for (let i = 0; i < this.filterDefinitions.length; i++) {
      let definition = this.filterDefinitions[i];
      let data: any;
      if (!definition.rangeDivisor && !definition.dateDiff && !definition.ranges) {
        data = this.filterDimensions[i].group().all();
      } else if (definition.rangeDivisor) {
        data = this.filterDimensions[i]
          .group((x) => {
            let val = Math.ceil((x || 0) / definition.rangeDivisor) * definition.rangeDivisor;
            return val || 1;
          })
          .all();
      } else if (definition.dateDiff) {
        data = this.filterDimensions[i]
          .group((x) => {
            let today = moment().startOf('day');
            let xDate = moment(x).startOf('day');
            let days = xDate.diff(today, 'days');

            //console.log("x: ", x, " - xDate:", xDate.toDate(), " - days: ", days);
            return days;
          })
          .all();
      } else if (definition.ranges) {
        data = this.filterDimensions[i]
          .group((x) => {
            let val = x || 1;
            let groupVal: number = undefined;

            for (let k = 0; k < definition.ranges.length; k++) {
              let range = definition.ranges[k];

              if (val >= range.min && val < range.max) {
                //console.log("Value ", val, " is within range ", definition.ranges[k], " setting groupVal to: ", range.groupValue);
                groupVal = range.groupValue;
                break;
              }
            }

            if (!groupVal) {
              //console.log("Value ", val, " outside any group range definition");
              return val;
            } else {
              return groupVal;
            }
          })
          .all();
      }

      //console.log(`Data result for index ${i}: ${data}`);
      //console.log("Data result for index ", i, " :", data);
      //console.log("selected filters: ", this.selectedFilters[i], " - data: ", data);
      let dataArray = data.filter(
        (x) =>
          x.value > 0 ||
          (this.selectedFilters[i]
            ? this.selectedFilters[i]
                .map((x) => {
                  return x.value;
                })
                .indexOf(x.key) >= 0
            : false),
      );
      //console.log("selected filters: ", this.selectedFilters[i], " - data: ", data, " - dataArray: ", dataArray);

      this.filterGroups[i] = {
        name: definition.title,
        data: dataArray,
        noRangeCheck: definition.noRangeCheck,
        selectItems: dataArray.map((x) => {
          if (!definition.rangeDivisor && !definition.ranges) {
            let lbl: string = x.key;

            if (definition.isDate) {
              lbl = moment(x.key).startOf('day').format('ddd DD MMM YYYY');
            } else if (definition.dateDiff) {
              lbl = `${x.key} days`;
            }

            return {
              label: lbl,
              count: x.value,
              value: definition.isDate ? moment(x.key).startOf('day').toISOString() : x.key,
            } as SelectItem;
          } else if (definition.rangeDivisor) {
            let numVal = parseFloat(x.key);

            //console.log("numval: ", numVal, " divisor: ", definition.divisor);

            // calculate label by value
            if (numVal <= definition.rangeDivisor) {
              return {
                label: `Up to ${definition.rangeDivisor}${definition.descriptor}`,
                count: x.value,
                value: x.key,
              } as SelectItem;
            } else {
              let min = numVal;
              let max = numVal + definition.rangeDivisor;
              let lbl = `${min}-${max}${definition.descriptor}`;

              return {
                label: lbl,
                count: x.value,
                value: x.key,
              } as SelectItem;
            }
          } else if (definition.ranges) {
            for (let j = 0; j < definition.ranges.length; j++) {
              if (definition.ranges[j].groupValue == x.key) {
                return {
                  label: definition.ranges[j].label,
                  count: x.value,
                  value: x.key,
                } as SelectItem;
              }
            }

            return {
              label: x.key,
              count: x.value,
              value: x.key,
            } as SelectItem;
          }
        }),
      };

      if (!this.filterGroups[i].noRangeCheck) {
        //console.log("Checking range state for: ", this.filterGroups[i]);
        this.filterGroups[i].selectItems.forEach((x) => {
          x.dataOutOfRange = this.dataOutOfRange(x);
        });
      }

      //Set isSelected checkbox flag
      this.setIsSelectedFlag();

      //console.log("Calling reset items from setSelections");
      this.resetSelectedItems(i);

      // add the selected items at top of list then the unselected
      this.orderSelectedUnselectedItems(i);
    }
  }

  private setIsSelectedFlag() {
    this.filterGroups.forEach((filter, i) => {
      filter.isSelected = Boolean(
        this.selectedFilters?.[i] && filter.selectItems.length === this.selectedFilters[i].length,
      );
    });
  }

  orderSelectedUnselectedItems(index: number) {
    let reordered = [];
    let selectedFilters = this.filterGroups[index].selectItems.filter((x) =>
      this.selectedFilters[index] ? this.selectedFilters[index].indexOf(x) >= 0 : false,
    );
    let unselectedFilters = this.filterGroups[index].selectItems.filter((x) =>
      this.selectedFilters[index] ? this.selectedFilters[index].indexOf(x) == -1 : false,
    );

    reordered.push(...selectedFilters, ...unselectedFilters);

    this.filterGroups[index].selectItems = reordered;
  }

  applyFilter(tableIndex: number, value: string, field: string, method): void {
    let facetTable = this.facetTables.toArray()[tableIndex];

    //console.log("Filtering table ", facetTable, " using: ", value, " [", field, "] and method (", method, ")");
    facetTable.filter(value, field, method);

    // store the filter in the definitions array so it can be reapplied when filters are refreshed
    this.filterDefinitions[tableIndex].tableFilter = { value, field, method };
    // console.log("Setting filter in filter group {", tableIndex, "}: ", this.filterDimensions[tableIndex]);

    //console.log("Results of filter: ", facetTable);
  }

  handleFilter(event, total) {
    //console.log("Table filtered: ", event, " - total: ", total);
  }

  resetSelectedItems(i: number) {
    //console.log("Resetting selected items [", i, "], selected filters: ", this.selectedFilters[i]);

    // reset selected items to reflect current filter
    let resetItems = [];

    for (let j = 0; j < this.filterGroups[i].selectItems.length; j++) {
      let item = this.filterGroups[i].selectItems[j];

      if (this.selectedFilters[i] && typeof this.selectedFilters[i].forEach === 'function') {
        this.selectedFilters[i].forEach((x) => {
          //console.log("Testing selected filter [", i, "]: ", x, " against item: ", item);

          if (x.value == item.value) {
            //console.log("Adding item ", item, " to filter selection");
            resetItems.push(item);
          }
        });
      }
    }

    this.selectedFilters[i] = resetItems;
  }

  loadFromStorage() {
    this.logger.trace('loadFromStorage()');
    let stored = JSON.parse(localStorage.getItem('allInbox-Selections'));
    if (stored) {
      //console.trace('selectedFilters:', stored);

      // parse selected filters from stored values
      this.currentSelection = stored;
      this.parseLoadedFilters();
    }

    this.setFiltersFromSelection();
  }

  parseLoadedFilters() {
    if (!this.currentSelection || this.currentSelection.length == 0) {
      return;
    }

    this.selectedFilters.forEach((filter, index) => {
      // do we have a current selection corresponding to this filter?
      const defName = this.filterDefinitions[index].propertyName;
      let sel = this.currentSelection.find((x) => x.definitionName == defName);
      if (sel) {
        // get index
        let selIndex = this.filterDefinitions.findIndex((x) => x.propertyName == defName);

        sel.values.forEach((val) => {
          let existing = this.filterGroups[selIndex].selectItems.find((x) => x.value == val);
          if (existing) {
            filter.push(existing);
          }
        });
      } else {
        filter = [];
      }
    });
  }

  setFiltersFromSelection() {
    this.settingFilters = true;

    setTimeout(() => {
      // Call setTimeout to run code asynchronously
      for (let i = 0; i < this.selectedFilters.length; i++) {
        this.setDimensionFilter(i);
        //console.log("Calling resetSelectedItems from loadFromStorage");
        this.resetSelectedItems(i);
      }

      //console.log("Populating from selected with timeout");
      this.populateWorkingSet(false);

      // force detection to render the component as we're using 'OnPush' detection strategy to speed up the agm-map
      this.changeDetectorRef.detectChanges();

      this.settingFilters = false; // Reset before leaving callback method
      this.noRowReset = false;
    }, 10);
  }

  saveToStorage() {
    this.currentSelection = [];
    this.selectedFilters.forEach((filter, index) => {
      if (filter && filter.length > 0) {
        this.currentSelection.push({
          definitionName: this.filterDefinitions[index].propertyName,
          values: filter.map((x) => x.value),
        });
      }
    });

    localStorage.setItem('allInbox-Selections', JSON.stringify(this.currentSelection));
    //console.log("Selected filters saved: ", this.selectedFilters);
  }

  viewJob(job: IFacetedInboxTask, $event) {
    const url = `app/jobscreen/${job.jobId}`;
    if ($event.ctrlKey || $event.metaKey) {
      window.open(url, '_blank');
    } else {
      void this.router.navigate([url]);
    }
  }

  selectedJobChanged(event) {
    //console.log("Selected job: ", this.selectedJob, "  - event: ", event);

    // fit map bounds to selected job start and end points
    if (this.showMap) this.setMapsContent();
  }

  vehicleCountPostfix(item, value) {
    return this.itemsCountPostfix(item, value, item.vehiclesNum);
  }

  itemsCountPostfix(item, value, count) {
    if (value == null || value == '' || count < 2) return value;
    return value + ' (+' + (count - 1) + ')';
  }

  sortFilter(index: number) {
    this.sortType[index] = !this.sortType[index];
    let filterGroup = this.filterGroups[index];

    if (this.sortType[index]) {
      filterGroup.selectItems = orderBy(
        filterGroup.selectItems,
        (o) => {
          return o.count;
        },
        'desc',
      );
    } else {
      filterGroup.selectItems = orderBy(
        filterGroup.selectItems,
        (o) => {
          return o.value;
        },
        'asc',
      );
    }

    // set selecteditem positions to top of array
    this.orderSelectedUnselectedItems(index);

    //console.log("Calling resetSelectedItems from sortFilter");
    this.resetSelectedItems(index);
  }

  // if selections are specified they will be set after clear
  clearAllFilters(selections = null) {
    this.settingFilters = true;

    setTimeout(() => {
      // Call setTimeout to run code asynchronously
      this.selectedFilters.fill(null);
      this.clearDimensions();

      //console.log("Populating from clear with timeout");
      this.populateWorkingSet();

      this.changeDetectorRef.detectChanges();
      this.settingFilters = false; // Reset before leaving callback method

      if (selections) {
        this.currentSelection = selections;
        this.parseLoadedFilters();
        //this.selectedFilters = selections;
        this.setFiltersFromSelection();
      }
    }, 10);
  }

  dataOutOfRange(data): boolean {
    let format = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
    let date = moment(data.value, format, true);
    let isDate = date.isValid();

    if (isDate) {
      return date < moment().subtract(1, 'd');
    } else if (this.isNumeric(data.value)) {
      return data.value < 0;
    }

    return false;
  }

  isNumeric(num) {
    num = '' + num; //coerce num to be a string
    let result = !isNaN(num) && !isNaN(parseFloat(num));
    return result;
  }

  getPickupDropoffDiffHours(item, isPickup): string {
    let from = isPickup ? item.earliestPickupDate : item.earliestDropOffDate;
    let to = isPickup ? item.latestPickupDate : item.latestDropOffDate;

    let diff = moment(to).diff(from, 'hours');
    return `${diff}hrs`;
  }

  agmMapReady(event: any) {
    if (this.showLogging) {
      console.log('** Google maps ready.. ', event);
    }

    let that = this;

    this.pickupIconStyles = [
      {
        textColor: 'white',
        url: 'assets/images/PickupM1.png',
        height: 39,
        width: 40,
      },
      {
        textColor: 'white',
        url: 'assets/images/PickupM2.png',
        height: 45,
        width: 46,
      },
      {
        textColor: 'white',
        url: 'assets/images/PickupM3.png',
        height: 49,
        width: 50,
      },
      {
        textColor: 'white',
        url: 'assets/images/PickupM4.png',
        height: 58,
        width: 59,
      },
      {
        textColor: 'white',
        url: 'assets/images/PickupM5.png',
        height: 67,
        width: 68,
      },
    ];

    this.dropoffIconStyles = [
      {
        textColor: 'white',
        url: 'assets/images/DropoffM1.png',
        height: 39,
        width: 40,
      },
      {
        textColor: 'white',
        url: 'assets/images/DropoffM2.png',
        height: 45,
        width: 46,
      },
      {
        textColor: 'white',
        url: 'assets/images/DropoffM3.png',
        height: 49,
        width: 50,
      },
      {
        textColor: 'white',
        url: 'assets/images/DropoffM4.png',
        height: 58,
        width: 59,
      },
      {
        textColor: 'white',
        url: 'assets/images/DropoffM5.png',
        height: 67,
        width: 68,
      },
    ];

    this.gmap = event.data.map;
    this.mapLoaded = true;

    //console.log('Updating map...');

    this.gmap.addListener('tilesloaded', () => {
      that.mapUpdating = false;
      console.log('Maps loaded, type: TILES - mapUpdating', that.mapUpdating);
      this.changeDetectorRef.detectChanges();
      this.changeDetectorRef.markForCheck();
    });
    this.gmap.addListener('idle', () => {
      that.mapUpdating = false;
      console.log('Maps loaded, type: IDLE');
      this.changeDetectorRef.detectChanges();
      this.changeDetectorRef.markForCheck();
    });

    this.heatmap = new google.maps.visualization.HeatmapLayer({
      map: event.data.map,
      data: [],
      radius: 20,
    });

    this.fitMapToBounds();
  }

  markJobForCombine() {
    let jobId = this.selectedJob.jobId;

    this.vmeInboxjobsService.markJobForCombine(jobId).then(() => {
      // todo: show toast to signify job marked for combination
      this.onGridAction.emit([
        this.type_inboxCombine,
        this.type_inboxSupplierToBeConfirmed,
        this.type_inboxDeploy,
      ]);
    });
  }

  deployJob() {
    let jobId = this.selectedJob.jobId;
    this.router.navigate(['app/job-deployment/' + jobId]);
  }

  supplierSchedule() {
    let jobId = this.selectedJob.jobId;
    this.router.navigate(['app/supplier-schedule-view/' + jobId]);
  }

  downloadToCSV() {
    this.vmeInboxjobsService.downloadAllTasksToCSV(this.workingSet);
  }

  getSelectedFiltersString(i: number) {
    return this.selectedFilters[i]
      .map(
        (x) =>
          '<div style="margin-top: 0px; margin-bottom: 4px; padding-top: 0px; padding-bottom: 4px; border-bottom: 1px solid #666;">' +
          x.label +
          '</div>',
      )
      .join('');
  }

  setFiltersFromLoadedSearch(selections: any) {
    //console.log("Loaded Selections: ", selections);

    this.clearAllFilters(selections);
  }

  markPriority() {
    this.isLoading = true;

    this.vmeInboxjobsService.markPriorityDeployment([this.selectedJob.jobId]).then(() => {
      let task = this.allData.find((x) => x.id == this.selectedJob.id);
      task.priorityDeployment = this.selectedJob.priorityDeployment == 'Yes' ? 'No' : 'Yes';

      this.noRowReset = true;

      this.setDimensions();
      this.setFiltersFromSelection();

      this.isLoading = false;
    });
  }

  async onShowAllJobsChanged(event: any) {
    await this.load();
  }

  async onShowInspectionTasksChanged(event: any) {
    localStorage.setItem('showInspectionTasks', event.checked);
    await this.load();
  }

  rowHasDelayedTask(item: IFacetedInboxTask) {
    //console.log('Checking for delayed task: ', item);

    return item.isDelayed == 'Yes';
  }

  onSelectAllClick(index: number, isSelected: boolean) {
    if (isSelected) {
      this.selectAll(index);
    } else {
      this.deSelectAll(index);
    }
    this.filterGroups[index].isSelected = isSelected;
  }

  selectAll(index: number) {
    const activeFilter = this.filterDefinitions[index].tableFilter;
    if (activeFilter) {
      const facetTable = this.facetTables.toArray()[index];
      const filteredItems = facetTable.filteredValue || this.filterGroups[index].selectItems;
      this.selectedFilters[index] = filteredItems;
    } else {
      this.selectedFilters[index] = this.filterGroups[index].selectItems;
    }
    this.changeDetectorRef.detectChanges();
    this.clearDimensions();
    this.populateWorkingSet();
  }

  deSelectAll(index: number) {
    this.selectedFilters[index] = null;
    this.clearDimensions();
    this.populateWorkingSet();
  }
}
